import { useState, useEffect } from 'react';
import { apiFetch } from '../../../lib/api';
import type { StorageProviderData } from './StorageProviderStep';
import type { GitHubInfraData } from './GitHubInfraStep';
import type { InfrastructureModeData } from './Step2InfrastructureMode';
import type { BYOCCredentialsData } from './BYOCCredentialsStep';
import type { Step2Data } from './Step2DeploymentPolicy';
import type { QAPolicyData } from './Step3QAPolicy';
import type { AgentConfigData, AgentType } from './Step4AgentConfig';
// PHASE_AGENTS is the shared source of truth — also consumed by the API
// (services/agentModelConfig.ts) and the /settings/agent-models page.
// Relative import (not `@blueprint/shared`) because apps/web doesn't yet
// have the shared workspace wired as a runtime dep; the physical file is
// still a single source of truth for both sides.
import { PHASE_AGENTS } from '../../../../../../packages/shared/src/phase-agents.ts';

// ── Types ─────────────────────────────────────────────────────────────────────

type Channel = 'in_app' | 'email';

interface NotifyOnEvents {
  amendment_proposed:  boolean;
  amendment_approved:  boolean;
  artefact_approved:   boolean;
  phase_handoff:       boolean;
  gate_failure:        boolean;
  gate_override:       boolean;
  invitation_received: boolean;
}

export interface NotificationPrefs {
  channels:            Channel[];
  emailDomain:         string | null;
  notifyOnEvents:      NotifyOnEvents;
  dataRetentionDays:   30 | 90 | 365 | -1;
  gdprDeletionEnabled: boolean;
}

export interface Step5Data {
  notificationPrefs: NotificationPrefs;
}

interface Props {
  tenantId:       string;
  onStepComplete: (data: Step5Data) => void;
  savedData?:     Step5Data;
  allStepData:    Record<string, unknown>;
  allSteps:       Array<{ id: string; label: string }>;
  onEditStep:     (index: number) => void;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const helpStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  color: 'var(--text-secondary)',
  marginTop: 4,
  lineHeight: 1.5,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--surface-alt)',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '16px 18px',
  background: 'var(--surface-alt)',
  marginBottom: 12,
};

const fieldGap: React.CSSProperties = { marginBottom: 20 };

// ── Toggle ────────────────────────────────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 0',
      }}
    >
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 36, height: 20, borderRadius: 10,
          background: checked ? 'var(--primary)' : 'var(--border)',
          position: 'relative', flexShrink: 0, cursor: 'pointer',
          transition: 'background 150ms',
        }}
      >
        <div style={{
          position: 'absolute', top: 2, left: checked ? 18 : 2,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 150ms', boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
        }} />
      </div>
      <div>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>
          {label}
        </span>
        {description && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '1px 0 0' }}>
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

// ── RadioGroup ────────────────────────────────────────────────────────────────

function RadioGroup<T extends string | number>({
  name,
  options,
  value,
  onChange,
}: {
  name: string;
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {options.map(opt => {
        const selected = value === opt.value;
        return (
          <label
            key={String(opt.value)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
              background: selected ? 'rgba(124,58,237,0.06)' : 'var(--surface-alt)',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)', fontSize: 14,
              fontWeight: selected ? 600 : 400,
              color: selected ? 'var(--primary)' : 'var(--text)',
              transition: 'border-color 150ms, background 150ms',
            }}
          >
            <input
              type="radio"
              name={name}
              checked={selected}
              onChange={() => onChange(opt.value)}
              style={{ accentColor: 'var(--primary)' }}
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

// ── Coverage badge (for review card) ─────────────────────────────────────────

function CoverageBadge({ value }: { value: number }) {
  let color: string, bg: string, label: string;
  if (value < 60)      { color = 'var(--error)';   bg = 'rgba(239,68,68,0.1)';   label = 'Low'; }
  else if (value < 80) { color = 'var(--warning)'; bg = 'rgba(245,158,11,0.1)'; label = 'Standard'; }
  else                 { color = 'var(--success)'; bg = 'rgba(34,197,94,0.1)';  label = 'High'; }
  return (
    <span style={{
      display: 'inline-block', padding: '1px 7px', borderRadius: 4,
      fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
      color, background: bg, letterSpacing: '0.04em', textTransform: 'uppercase',
    }}>
      {label}
    </span>
  );
}

// ── Review card ───────────────────────────────────────────────────────────────

function ReviewCard({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
          {title}
        </h4>
        <button
          type="button"
          onClick={onEdit}
          style={{
            fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
            padding: '4px 10px',
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          Edit
        </button>
      </div>
      {children}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 13, fontFamily: 'var(--font-body)' }}>
      <span style={{ color: 'var(--text-secondary)', minWidth: 160, flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// ── Agent type label map ──────────────────────────────────────────────────────

const AGENT_LABELS: Record<AgentType, string> = {
  PM_AGENT:           'PM Agent',
  ARCHITECTURE_AGENT: 'Architecture Agent',
  UX_AGENT:           'UX Design Agent',
  SPRINT_AGENT:       'Sprint Agent',
  DEV_AGENT:          'Dev Agent',
  REVIEW_AGENT:       'Review Agent',
  QA_AGENT:           'QA Agent',
  DEVOPS_AGENT:       'DevOps Agent',
  RETRO_AGENT:        'Retro Agent',
  PHASE_GATE_AGENT:   'Phase Gate Agent',
  BOOTSTRAP_AGENT:    'Bootstrap Agent',
};

// PHASE_AGENTS is now imported from packages/shared/src/phase-agents.ts —
// the same definition is consumed by the API and the /settings/agent-models
// page (CFG.LLM.A1). See the import block at the top of this file.

const SIGN_OFF_LABELS: Record<QAPolicyData['signOffRole'], string> = {
  QA_ENGINEER:     'QA Engineer',
  TECH_LEAD:       'Tech Lead',
  PRODUCT_MANAGER: 'Product Manager',
  AUTOMATED_GATE:  'Automated gate',
};

const DEFAULT_NOTIFY_EVENTS: NotifyOnEvents = {
  amendment_proposed:  true,
  amendment_approved:  true,
  artefact_approved:   true,
  phase_handoff:       true,
  gate_failure:        true,
  gate_override:       true,
  invitation_received: true,
};

// ── Main component ────────────────────────────────────────────────────────────

export default function Step5ReviewConfirm({
  tenantId,
  onStepComplete,
  savedData,
  allStepData,
  allSteps,
  onEditStep,
}: Props) {
  // Notification prefs state
  const [channelMode,     setChannelMode]     = useState<'in_app' | 'in_app_email'>(
    savedData?.notificationPrefs?.channels.includes('email') ? 'in_app_email' : 'in_app',
  );
  const [emailDomain,     setEmailDomain]     = useState(savedData?.notificationPrefs?.emailDomain ?? '');
  const [notifyEvents,    setNotifyEvents]    = useState<NotifyOnEvents>(
    savedData?.notificationPrefs?.notifyOnEvents ?? DEFAULT_NOTIFY_EVENTS,
  );
  const [retention,       setRetention]       = useState<30 | 90 | 365 | -1>(
    savedData?.notificationPrefs?.dataRetentionDays ?? 365,
  );
  const [gdpr,            setGdpr]            = useState(savedData?.notificationPrefs?.gdprDeletionEnabled ?? false);

  const [completing, setCompleting] = useState(false);
  const [saveError,  setSaveError]  = useState('');

  // Load existing prefs on mount
  useEffect(() => {
    apiFetch<
      | { configured: false; defaults: NotificationPrefs }
      | { configured: true } & NotificationPrefs
    >(`/tenants/${tenantId}/notification-preferences`)
      .then(result => {
        const prefs = result.configured
          ? (result as { configured: true } & NotificationPrefs)
          : result.defaults;
        setChannelMode(prefs.channels.includes('email') ? 'in_app_email' : 'in_app');
        setEmailDomain(prefs.emailDomain ?? '');
        setNotifyEvents(prefs.notifyOnEvents as NotifyOnEvents ?? DEFAULT_NOTIFY_EVENTS);
        setRetention((prefs.dataRetentionDays as 30 | 90 | 365 | -1) ?? 365);
        setGdpr(prefs.gdprDeletionEnabled ?? false);
      })
      .catch(() => {});
  }, [tenantId]);

  const focusRing = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.boxShadow   = '0 0 0 3px rgba(124,58,237,0.12)';
    e.currentTarget.style.borderColor = 'var(--primary)';
  };
  const blurRing = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.boxShadow   = 'none';
    e.currentTarget.style.borderColor = 'var(--border)';
  };

  const toggleEvent = (key: keyof NotifyOnEvents) => {
    setNotifyEvents(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (channelMode === 'in_app_email' && !emailDomain.trim()) {
      setSaveError('Email domain is required when email notifications are enabled.');
      return;
    }
    setCompleting(true);
    setSaveError('');

    const prefs: NotificationPrefs = {
      channels:            channelMode === 'in_app_email' ? ['in_app', 'email'] : ['in_app'],
      emailDomain:         channelMode === 'in_app_email' ? emailDomain.trim() : null,
      notifyOnEvents:      notifyEvents,
      dataRetentionDays:   retention,
      gdprDeletionEnabled: gdpr,
    };

    try {
      // 1. Save notification prefs
      await apiFetch(`/tenants/${tenantId}/notification-preferences`, {
        method: 'POST',
        body:   JSON.stringify(prefs),
      });

      // 2. Mark onboarding complete + save infrastructure mode
      const infraMode = (allStepData['infrastructure_mode'] as InfrastructureModeData | undefined)?.infrastructureMode;
      await apiFetch(`/tenants/${tenantId}`, {
        method: 'PATCH',
        body:   JSON.stringify({
          onboardingStatus: 'COMPLETE',
          ...(infraMode ? { infrastructureMode: infraMode } : {}),
        }),
      });

      // 3. Trigger bootstrap provisioning for all existing projects (fire-and-forget)
      void apiFetch<{ data?: { id: string }[] }>('/projects').then(res => {
        for (const project of res.data ?? []) {
          void apiFetch(`/projects/${project.id}/bootstrap`, {
            method: 'POST',
            body:   JSON.stringify({}),
          }).catch(() => {});
        }
      }).catch(() => {});

      onStepComplete({ notificationPrefs: prefs });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to complete setup. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  // ── Step data helpers ───────────────────────────────────────────────────────
  // B1 split old step0 (storage+github) into step0 (storage) + step1 (github).
  // Mode moved from step1→step2; all subsequent steps shift by +1.
  // BYOC: 9 steps (0-8). Managed: 8 steps (0-7).

  const stepStorage = allStepData['storage'] as StorageProviderData | undefined;
  const stepGitHub  = allStepData['github'] as GitHubInfraData | undefined;
  const stepMode    = allStepData['infrastructure_mode'] as InfrastructureModeData | undefined;
  const isBYOC      = stepMode?.infrastructureMode === 'BYOC';
  const byocCreds   = isBYOC ? allStepData['byoc_credentials'] as BYOCCredentialsData | undefined : undefined;
  const step2       = allStepData['deployment'] as Step2Data | undefined;
  const step3       = allStepData['qa_policy'] as QAPolicyData | undefined;
  const step4       = allStepData['agent_config'] as AgentConfigData | undefined;
  const editStep    = (id: string) => {
    const idx = allSteps.findIndex(s => s.id === id);
    if (idx >= 0) onEditStep(idx);
  };

  // Compare via string to avoid type friction between the broader local
  // AgentType (10 agents incl. retro/bootstrap/phase-gate) and the shared
  // PhaseAgentType (the user-facing agents — 8 since UX.D1). Runtime
  // behaviour is identical; this comment just tracks the divergence.
  const phaseAgentTypes   = new Set<string>(PHASE_AGENTS.map(a => a.agentType));
  const phaseConfigs      = step4?.configs?.filter(c => phaseAgentTypes.has(c.agentType as string)) ?? [];
  const totalAgents       = PHASE_AGENTS.length;
  const activeAgentCount  = phaseConfigs.length > 0
    ? phaseConfigs.filter(c => c.isActive).length
    : totalAgents;
  const manualCount       = totalAgents - activeAgentCount;
  const disabledAgents    = phaseConfigs.filter(c => !c.isActive).map(c => AGENT_LABELS[c.agentType]);
  const customPromptCount = step4?.configs
    ? step4.configs.filter(c => c.systemPromptOverride).length
    : 0;

  const EVENT_LABELS: Array<{ key: keyof NotifyOnEvents; label: string }> = [
    { key: 'amendment_proposed',  label: 'Amendment proposed' },
    { key: 'amendment_approved',  label: 'Amendment approved' },
    { key: 'artefact_approved',   label: 'Artefact approved' },
    { key: 'phase_handoff',       label: 'Phase handoff completed' },
    { key: 'gate_failure',        label: 'Phase gate failure' },
    { key: 'gate_override',       label: 'Gate override used' },
    { key: 'invitation_received', label: 'Invitation received' },
  ];

  return (
    <form onSubmit={e => void handleComplete(e)}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
        Review &amp; Confirm
      </h3>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 28 }}>
        Configure notifications, review your settings, then complete setup.
      </p>

      {/* ── Notification channels ─────────────────────────────────────────── */}
      <div style={fieldGap}>
        <label style={labelStyle}>Notification channels</label>
        <RadioGroup
          name="channelMode"
          value={channelMode}
          onChange={setChannelMode}
          options={[
            { value: 'in_app',       label: 'In-app only' },
            { value: 'in_app_email', label: 'In-app + Email' },
          ]}
        />
      </div>

      {channelMode === 'in_app_email' && (
        <div style={fieldGap}>
          <label style={labelStyle}>
            Organisation email domain <span style={{ color: 'var(--error)' }}>*</span>
          </label>
          <input
            type="text"
            value={emailDomain}
            onChange={e => setEmailDomain(e.target.value)}
            placeholder="yourcompany.com"
            style={inputStyle}
            onFocus={focusRing}
            onBlur={blurRing}
          />
          <p style={helpStyle}>Notifications will be sent to addresses at this domain.</p>
        </div>
      )}

      {/* ── Event notifications ───────────────────────────────────────────── */}
      <div style={fieldGap}>
        <label style={labelStyle}>Event notifications</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 16px' }}>
          {EVENT_LABELS.map(({ key, label }) => (
            <Toggle
              key={key}
              checked={notifyEvents[key]}
              onChange={() => toggleEvent(key)}
              label={label}
            />
          ))}
        </div>
      </div>

      {/* ── Data retention ────────────────────────────────────────────────── */}
      <div style={fieldGap}>
        <label style={labelStyle}>How long should audit logs and notifications be retained?</label>
        <RadioGroup
          name="retention"
          value={retention}
          onChange={v => setRetention(v as 30 | 90 | 365 | -1)}
          options={[
            { value: 30,  label: '30 days' },
            { value: 90,  label: '90 days' },
            { value: 365, label: '1 year' },
            { value: -1,  label: 'Indefinite' },
          ]}
        />
        <p style={helpStyle}>Older records are automatically purged after this period.</p>
      </div>

      {/* ── GDPR cascade deletion ─────────────────────────────────────────── */}
      <div style={fieldGap}>
        <label style={labelStyle}>GDPR cascade deletion</label>
        <Toggle
          checked={gdpr}
          onChange={setGdpr}
          label="Enable cascade deletion"
          description="Deleting a user removes all their data within this tenant"
        />
        {gdpr && (
          <div style={{
            marginTop: 8, padding: '10px 14px',
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--warning)',
          }}>
            ⚠ This action is irreversible. Deleted user data cannot be recovered.
          </div>
        )}
        <p style={helpStyle}>
          When enabled, deleting a user removes all their data within this tenant. Required for
          GDPR Article 17 compliance.
        </p>
      </div>

      {/* ── Divider ───────────────────────────────────────────────────────── */}
      <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '28px 0' }} />

      {/* ── Review summary ────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          Review your configuration
        </h2>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
          Check everything looks right before completing setup.
        </p>

        {/* Storage — B1: step 0 (was part of old Infrastructure step) */}
        <ReviewCard title="Storage" onEdit={() => editStep('storage')}>
          <ReviewRow label="Provider" value={stepStorage?.storageProvider?.replace('_', ' ') ?? '—'} />
        </ReviewCard>

        {/* GitHub — B1: step 1 (was part of old Infrastructure step) */}
        <ReviewCard title="GitHub" onEdit={() => editStep('github')}>
          <ReviewRow label="Organisation" value={stepGitHub?.githubOrg || 'Not configured'} />
          <ReviewRow label="Token"        value={stepGitHub?.githubTokenSet ? '••••••[saved]' : 'Not configured'} />
        </ReviewCard>

        {/* Deployment Mode — step 2 (was step 1) */}
        <ReviewCard title="Deployment Mode" onEdit={() => editStep('infrastructure_mode')}>
          <ReviewRow label="Mode" value={isBYOC ? 'Bring Your Own Cloud' : 'BluePrint Managed'} />
        </ReviewCard>

        {/* BYOC Credentials (only shown for BYOC flow) — step 3 (was step 2) */}
        {isBYOC && (
          <ReviewCard title="Cloud Credentials" onEdit={() => editStep('byoc_credentials')}>
            <ReviewRow label="Provider"      value={byocCreds?.provider ?? '—'} />
            <ReviewRow label="Region"        value={byocCreds?.region || 'Not configured'} />
            <ReviewRow label="Key ID"        value={byocCreds?.maskedKeyId || 'Not configured'} />
            <ReviewRow label="Custom domain" value={byocCreds?.customDomain || 'Not configured'} />
          </ReviewCard>
        )}

        {/* Deployment — only show for BYOC flow (Managed has no deployment step) */}
        {step2?.environments && (
        <ReviewCard title="Deployment Policy" onEdit={() => editStep('deployment')}>
          <ReviewRow label="Environments"   value={
            step2.environments.length > 0
              ? `${step2.environments.length} (${step2.environments.map(e => e.name).filter(Boolean).join(', ') || '—'})`
              : '—'
          } />
          <ReviewRow label="Promotion model" value={step2?.promotionModel?.replace(/_/g, ' ') ?? '—'} />
          <ReviewRow label="CI provider"     value={step2?.ciProvider?.replace(/_/g, ' ') ?? '—'} />
          <ReviewRow label="Runbook required" value={step2?.requiresRunbook ? 'Yes' : 'No'} />
          <ReviewRow label="CAB required"     value={step2?.requiresCAB ? 'Yes' : 'No'} />
        </ReviewCard>
        )}

        {/* QA Policy */}
        <ReviewCard title="QA Policy" onEdit={() => editStep('qa_policy')}>
          <ReviewRow label="Test types"          value={step3?.requiredTestTypes?.join(', ') ?? '—'} />
          <ReviewRow label="Coverage threshold"  value={
            step3
              ? <span>{step3.coverageThreshold}% <CoverageBadge value={step3.coverageThreshold} /></span>
              : '—'
          } />
          <ReviewRow label="Sign-off role"       value={step3 ? SIGN_OFF_LABELS[step3.signOffRole] : '—'} />
          <ReviewRow label="Block deploy"        value={step3?.blockDeployOnFailure === true ? 'Yes' : step3?.blockDeployOnFailure === false ? 'No' : '—'} />
        </ReviewCard>

        {/* Agent Config — index 6 in BYOC, 5 in Managed (see step list in WizardShell) */}
        <ReviewCard title="Agent Configuration" onEdit={() => editStep('agent_config')}>
          <ReviewRow label="Active agents"   value={
            activeAgentCount === totalAgents
              ? `All ${totalAgents} agents active`
              : `${activeAgentCount} of ${totalAgents} agents active \u00b7 ${manualCount} in manual mode`
          } />
          <ReviewRow label="Disabled agents" value={disabledAgents.length ? disabledAgents.join(', ') : 'All agents active'} />
          <ReviewRow label="Custom prompts"  value={customPromptCount > 0 ? `${customPromptCount} agent${customPromptCount !== 1 ? 's' : ''} configured` : 'None'} />
        </ReviewCard>
      </div>

      {/* Error banner */}
      {saveError && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: 'var(--error)',
        }}>
          {saveError}
        </div>
      )}

      {/* Complete Setup button */}
      <button
        type="submit"
        disabled={completing}
        style={{
          width: '100%',
          fontFamily: 'var(--font-body)',
          fontSize: 15,
          fontWeight: 700,
          padding: '13px 24px',
          background: completing ? 'var(--primary)' : 'var(--primary)',
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          cursor: completing ? 'not-allowed' : 'pointer',
          opacity: completing ? 0.7 : 1,
          transition: 'opacity 150ms',
        }}
      >
        {completing ? 'Setting up…' : 'Complete Setup'}
      </button>
    </form>
  );
}
